/**
 * Emblema da MPC.
 *
 * Só a taça da arte oficial, sem o nome desenhado embaixo. O nome da competição, onde ele aparece, é texto HTML ao lado —
 * assim ele quebra linha, herda a cor do tema, é lido por leitor de tela e pode
 * ser selecionado. Havia aqui uma segunda variante que servia uma arte com o
 * nome desenhado dentro: aos 30px do rodapé as três linhas viravam borrão, e ao
 * lado do nome escrito em texto o nome aparecia duas vezes.
 *
 * A altura é que manda, e a largura acompanha a proporção da taça — travá-la
 * num quadrado a encolheria para caber. Como ela é estreita, as alturas em uso
 * são maiores que as do escudo antigo, para a marca ocupar a mesma presença.
 *
 * O arquivo é servido de `public/` em vez de importado pelo bundler: assim o
 * navegador o busca em paralelo com o JavaScript, e a marca aparece sem esperar
 * o pacote da aplicação. É o mesmo desenho que alimenta o favicon, gerado por
 * `npm run logo` a partir de `src/assets/marca-mpc.png`.
 */

import { cn } from '@/lib/cn';

/**
 * Proporção da taça recortada (419x796 na arte), para reservar o espaço antes
 * de carregar. Se a arte mudar, `npm run logo` imprime a nova proporção.
 */
const PROPORCAO = 419 / 796;

interface Props {
  /** Altura em pixels. A largura sai da proporção da arte. */
  altura?: number;
  className?: string;
}

export function LogoMPC({ altura = 32, className }: Props) {
  const largura = Math.round(altura * PROPORCAO);

  return (
    <img
      src="/logo.webp"
      width={largura}
      height={altura}
      alt=""
      aria-hidden
      draggable={false}
      className={cn('shrink-0 object-contain select-none', className)}
      style={{ height: altura, width: largura }}
    />
  );
}

/**
 * O nome da competição, escrito ao lado da taça.
 *
 * A arte oficial traz o nome desenhado embaixo do emblema, mas o que o site
 * serve é só a taça (ver `tools/logo.mjs`): aos 52px do cabeçalho as três
 * linhas desenhadas viram borrão, e o nome como texto pode ser selecionado,
 * traduzido e lido por leitor de tela.
 *
 * Duas linhas, como na arte: "MD PRO CLUBS" e, embaixo, "CHAMPIONSHIP" em
 * limão e espaçada, ocupando a mesma largura da linha de cima. Em telas
 * estreitas some e fica só a taça — o nome inteiro empurraria os botões do
 * cabeçalho para fora.
 */
export function NomeDaMarca({ tamanho = 'md' }: { tamanho?: 'md' | 'lg' }) {
  const grande = tamanho === 'lg';

  return (
    <span className={cn('hidden leading-none select-none sm:block', grande ? 'ml-4' : 'ml-3')}>
      <span className={cn('mpc-display block text-paper', grande ? 'text-[1.6rem]' : 'text-[1.0625rem]')}>
        MD Pro Clubs
      </span>
      <span
        className={cn(
          'mt-1 block font-medium text-brand-ink',
          grande ? 'text-[0.8125rem] tracking-[0.42em]' : 'text-[0.5625rem] tracking-[0.34em]',
        )}
      >
        CHAMPIONSHIP
      </span>
    </span>
  );
}
